import { useCallback } from 'react'
import { useDashboardDrillStore } from '@/store/dashboardStore'
import type { DrillDescriptor } from '@/types/dashboards'

/** The element that opened the dialog, for focus restoration on close. */
let drillTrigger: HTMLElement | null = null

export function consumeDrillTrigger(): HTMLElement | null {
  const element = drillTrigger
  drillTrigger = null
  return element
}

/**
 * Opens the shared drill-down dialog.
 *
 * `open` tolerates an absent descriptor so callers can wire it straight to a
 * `Figure` without branching — a figure with no `drill` is simply not clickable.
 */
export function useDrillDown() {
  const isOpen = useDashboardDrillStore((s) => s.isOpen)
  const descriptor = useDashboardDrillStore((s) => s.descriptor)
  const openDrill = useDashboardDrillStore((s) => s.openDrill)
  const close = useDashboardDrillStore((s) => s.closeDrill)

  const open = useCallback(
    (next?: DrillDescriptor | null) => {
      if (!next) return
      // Remember what the user clicked so the dialog can hand focus back on
      // close. Captured here, at the single choke point every drill goes
      // through, rather than inside the dialog: reading document.activeElement
      // from the dialog's own effect is unreliable because the effect also
      // moves focus into the dialog, and StrictMode re-runs it — the second
      // pass then records the dialog's close button as the return target and
      // focus lands on <body>.
      drillTrigger = document.activeElement as HTMLElement | null
      openDrill(next)
    },
    [openDrill],
  )

  return { isOpen, descriptor, open, close }
}
